import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// List view: the state table. (The map lives on its own tab now — see MapPage.)
export default function StateList() {
  const navigate = useNavigate();
  const [states, setStates] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetch('/api/states')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setStates)
      .catch((err) => setError(err.message));
  }, []);

  const rows = useMemo(() => {
    if (!states) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? states.filter((s) => s.name.toLowerCase().includes(q) || s.abbr.toLowerCase().includes(q))
      : states;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [states, query]);
  const groups = [
    { country: 'US', title: 'United States', region: 'State', county: 'Counties', points: 'ZIP codes' },
    { country: 'CA', title: 'Canada', region: 'Province / territory', county: 'Census divisions', points: 'Municipalities' },
  ]
    .map((g) => ({ ...g, rows: rows.filter((s) => (s.country ?? 'US') === g.country) }))
    .filter((g) => g.rows.length);

  return (
    <div className="app">
      <header className="masthead">
        <div>
          <h1>Heat Watch</h1>
          <p className="sub">
            Wet bulb temperature and environmental conditions across the United States and Canada
          </p>
        </div>
      </header>

      {error && (
        <div className="banner err">
          <strong>Could not load states.</strong> {error}
        </div>
      )}

      {!states && !error && <div className="loading">Loading states…</div>}

      {states && (
        <>
          <div className="controls">
            <input
              className="search"
              placeholder="Filter by state or province…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="spacer" />
            <span className="muted" style={{ fontSize: 12 }}>
              {rows.length} of {states.length} states, provinces and territories
            </span>
          </div>

          {groups.map((g) => (
            <section key={g.country} style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 15, margin: '12px 0 8px' }}>{g.title}</h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th className="left">{g.region}</th>
                      <th>{g.county}</th>
                      <th>{g.points}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((s) => (
                      <tr key={s.abbr} onClick={() => navigate(`/${s.abbr}`)}>
                        <td className="left">
                          {s.name}
                          <span className="place">{s.abbr}</span>
                        </td>
                        <td>{s.countyCount}</td>
                        <td>{s.zipCount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <div className="muted" style={{ fontSize: 12, marginTop: 12, maxWidth: 760 }}>
            <p>
              Canada is covered by province/territory, census division and municipality (Statistics
              Canada 2021 boundaries), with weather, heat and air quality at every municipality. Work
              rules below are US-only and are not applied to Canadian locations.
            </p>
            <p style={{ marginTop: 8 }}>
              In the US, Heat Watch is a live weather-regulatory dashboard for outdoor work sites,
              covering all 50 states, DC, and every US ZIP code. Each ZIP is checked continuously
              against real weather data — wet bulb temperature, WBGT, heat index, wind chill,
              wildfire smoke (AQI), wind gusts, and precipitation — and scored against whichever
              jurisdiction actually governs it: a state&rsquo;s own heat, wildfire smoke, or wind
              standard where one has been researched (currently CA, WA, OR, MD, NV, and/or MN,
              depending on category), or the federal OSHA General Duty Clause advisory framework
              everywhere else. Every triggered rule shows its own citation and enforcing agency,
              so you can verify it against the source yourself rather than taking this dashboard&rsquo;s
              word for it.
            </p>
            <p style={{ marginTop: 8 }}>
              An industry selector on each county page swaps the rule set entirely, not just the
              label: Construction (the default), Sports &amp; Athletics (NCAA and High School),
              Agriculture, and the Wind, Solar, Oil &amp; Gas, and Hydropower energy sectors each
              have their own thresholds, citations, and action guidance — for example, Colorado
              has no construction-specific heat rule at all, but does have one for agricultural
              labor. Weather and air-quality data come from Open-Meteo. This dashboard is an
              operational planning aid, not legal advice or a compliance record.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
